import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause } from 'lucide-react';
import { motion, useAnimation } from 'framer-motion';
import { PremiumGate } from '@/components/PremiumGate';
import { useEntitlements } from '@/hooks/useEntitlements';

const ShapeBreather = () => {
  const navigate = useNavigate();
  const { hasAccess } = useEntitlements();
  const [isActive, setIsActive] = useState(false);
  const [phase, setPhase] = useState('inhale');
  const [petalCount, setPetalCount] = useState(8);
  const controls = useAnimation();

  useEffect(() => {
    if (!isActive) return;

    const interval = setInterval(() => {
      setPhase(current => current === 'inhale' ? 'exhale' : 'inhale');
    }, 4000);

    return () => clearInterval(interval);
  }, [isActive]);

  useEffect(() => {
    if (isActive) {
      controls.start({
        rx: phase === 'inhale' ? 120 : 60,
        ry: phase === 'inhale' ? 40 : 20,
        transition: { duration: 4, ease: 'easeInOut' }
      });
    } else {
      controls.stop();
    }
  }, [isActive, phase, controls]);

  if (!hasAccess('apps_premium')) {
    return <PremiumGate appName="Shape Breather" />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF0F5] to-[#FFE4E1] relative overflow-hidden">
      <div className="relative z-10 max-w-4xl mx-auto px-6 py-8">
        <header className="flex justify-between items-center mb-12">
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate('/dashboard')}
            className="bg-white text-[#2D3748] px-6 py-3 rounded-full font-semibold hover:bg-slate-100 transition-all flex items-center gap-2 shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          
          <h1 className="text-4xl font-bold text-[#2D3748]">Shape Breather</h1>
          
          <button
            data-testid="toggle-breathing"
            onClick={() => setIsActive(!isActive)}
            className="bg-gradient-to-r from-[#FF6B9D] to-[#FFB6C1] text-white px-6 py-3 rounded-full font-semibold hover:-translate-y-1 transition-all shadow-lg flex items-center gap-2"
          >
            {isActive ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            {isActive ? 'Stop' : 'Start'}
          </button>
        </header>

        <div className="flex items-center justify-center min-h-[500px]">
          <div className="relative">
            <svg width="400" height="400" viewBox="0 0 400 400">
              {Array.from({ length: petalCount }).map((_, i) => {
                const angle = (i * 360) / petalCount;
                return (
                  <motion.ellipse
                    key={i}
                    cx="200"
                    cy="200"
                    rx={phase === 'inhale' ? 120 : 60}
                    ry={phase === 'inhale' ? 40 : 20}
                    fill="url(#petal-gradient)"
                    opacity="0.8"
                    transform={`rotate(${angle} 200 200)`}
                    animate={controls}
                  />
                );
              })}
              <defs>
                <linearGradient id="petal-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#FFB6C1" />
                  <stop offset="100%" stopColor="#FF6B9D" />
                </linearGradient>
              </defs>
              <circle cx="200" cy="200" r="30" fill="#FFD700" />
            </svg>
            
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="text-4xl font-bold text-white drop-shadow-lg">
                {phase === 'inhale' ? 'Breathe In' : 'Breathe Out'}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 bg-white rounded-2xl p-6 text-center shadow-lg">
          <p className="text-[#4A5568] text-lg">
            Watch the flower expand and contract. Breathe in as it grows, breathe out as it shrinks.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ShapeBreather;